import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faSave, 
  faEdit, 
  faTrash, 
  faPlus, 
  faCheck, 
  faBan 
} from '@fortawesome/free-solid-svg-icons';
import { supabase } from '../lib/supabaseClient';

const ExpenseTypesModal = ({ isOpen, onClose, onUpdate }) => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    name_tr: '',
    name_en: '',
    name_ru: '',
    active: true
  });

  useEffect(() => {
    if (isOpen) {
      fetchAllTypes();
      resetForm();
    }
  }, [isOpen]);

  const fetchAllTypes = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('load_expenses_types')
        .select('*')
        .order('id', { ascending: true });

      if (error) throw error;
      setTypes(data || []);
    } catch (error) {
      console.error('Tipler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEditClick = (type) => {
    setEditingId(type.id);
    setFormData({
      label: type.label,
      name_tr: type.name_tr,
      name_en: type.name_en,
      name_ru: type.name_ru,
      active: type.active
    });
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      label: '',
      name_tr: '',
      name_en: '',
      name_ru: '',
      active: true
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        label: formData.label,
        name_tr: formData.name_tr,
        name_en: formData.name_en,
        name_ru: formData.name_ru,
        active: formData.active
      };

      let error;
      
      if (editingId) {
        const { error: updateError } = await supabase
          .from('load_expenses_types')
          .update(payload)
          .eq('id', editingId);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('load_expenses_types')
          .insert([payload]);
        error = insertError;
      }

      if (error) throw error;

      await fetchAllTypes();
      resetForm();
      if (onUpdate) onUpdate();

    } catch (err) {
      console.error('Kayıt hatası:', err);
      alert('İşlem sırasında bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <h3 className="text-xl font-bold text-gray-800">Masraf Tipleri Yönetimi</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Sol Kolon: Form */}
            <div className="lg:col-span-1">
              <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200 sticky top-0">
                <h4 className="font-semibold text-gray-700 mb-4 flex items-center">
                  {editingId ? <FontAwesomeIcon icon={faEdit} className="mr-2 text-blue-500"/> : <FontAwesomeIcon icon={faPlus} className="mr-2 text-green-500"/>}
                  {editingId ? 'Tipi Düzenle' : 'Yeni Tip Ekle'}
                </h4>
                
                <form onSubmit={handleSave} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Kod (Label)</label>
                    <input
                      type="text"
                      name="label"
                      value={formData.label}
                      onChange={handleInputChange}
                      placeholder="örn: hotel"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Türkçe (TR)</label>
                    <input
                      type="text"
                      name="name_tr"
                      value={formData.name_tr}
                      onChange={handleInputChange}
                      placeholder="Konaklama"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">İngilizce (EN)</label>
                    <input
                      type="text"
                      name="name_en"
                      value={formData.name_en}
                      onChange={handleInputChange}
                      placeholder="Accommodation"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Rusça (RU)</label>
                    <input
                      type="text"
                      name="name_ru"
                      value={formData.name_ru}
                      onChange={handleInputChange}
                      placeholder="Проживание"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2 pt-2">
                    <input
                      type="checkbox"
                      id="activeCheck"
                      name="active"
                      checked={formData.active}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                    />
                    <label htmlFor="activeCheck" className="text-sm text-gray-700 select-none">Aktif Kullanımda</label>
                  </div>

                  <div className="flex space-x-2 pt-2">
                    {editingId && (
                      <button
                        type="button"
                        onClick={resetForm}
                        className="flex-1 bg-gray-100 text-gray-600 py-2 rounded-lg hover:bg-gray-200 text-sm transition"
                      >
                        İptal
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={loading}
                      className={`flex-1 py-2 rounded-lg text-white text-sm transition flex items-center justify-center
                        ${editingId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}
                      `}
                    >
                      <FontAwesomeIcon icon={faSave} className="mr-2" />
                      {editingId ? 'Güncelle' : 'Kaydet'}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Sağ Kolon: Liste */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">ID</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Label</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">TR / EN</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">Durum</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase">İşlem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {types.map((type) => (
                      <tr key={type.id} className={`hover:bg-gray-50 transition ${editingId === type.id ? 'bg-blue-50' : ''}`}>
                        <td className="px-4 py-3 text-sm text-gray-500">#{type.id}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-700">{type.label}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div className="font-medium">{type.name_tr}</div>
                          <div className="text-xs text-gray-400">{type.name_en}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {type.active ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              Aktif
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                              Pasif
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleEditClick(type)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="Düzenle"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {types.length === 0 && !loading && (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-gray-500 text-sm">
                          Henüz kayıtlı masraf tipi yok.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseTypesModal;